/**
 * Dispatch route controls (tracker item 57): the route list hook, a route
 * select, the three-choice prompt shown when a Customer / Property default
 * changes, and the default-route panel on the Customer and Property pages.
 *
 * Route endpoints are ADMIN / OFFICE_MANAGER only, so callers enable these
 * for office roles only.
 */

import React, { useEffect, useRef, useState } from 'react';
import type { IDispatchApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from './formStyles';
import { Toast } from './Toast';
import { propagationChoices, routeChoices, routeDefaultNotice, routeName } from '../pages/routeUi';

export interface DispatchRoutesState {
  /** null while loading (or when disabled) */
  routes: types.DispatchRoute[] | null;
  error: string | null;
}

/** Loads the tenant's routes once. A failure (or a client without the call) leaves the page working. */
export function useDispatchRoutes(api: Partial<Pick<IDispatchApi, 'listDispatchRoutes'>>, enabled: boolean): DispatchRoutesState {
  const [state, setState] = useState<DispatchRoutesState>({ routes: null, error: null });
  useEffect(() => {
    if (!enabled) { setState({ routes: null, error: null }); return; }
    let active = true;
    Promise.resolve()
      .then(() => {
        if (!api.listDispatchRoutes) throw new Error('Routes are not available');
        return api.listDispatchRoutes();
      })
      .then((routes) => { if (active) setState({ routes, error: null }); })
      .catch((err) => { if (active) setState({ routes: [], error: getErrorMessage(err, 'Could not load dispatch routes') }); });
    return () => { active = false; };
  }, [api, enabled]);
  return state;
}

interface RouteSelectProps {
  id: string;
  label: string;
  /** '' or a leading option value, else a route id */
  value: string;
  onChange: (value: string) => void;
  routes: types.DispatchRoute[] | null;
  /** The saved route, kept selectable when inactive */
  currentId?: string | null;
  /** Options before the routes, e.g. [{ value: '', label: 'No default route' }] */
  leadingOptions: Array<{ value: string; label: string }>;
  disabled?: boolean;
  loadError?: string | null;
  hint?: React.ReactNode;
}

export function RouteSelect({ id, label, value, onChange, routes, currentId, leadingOptions, disabled, loadError, hint }: RouteSelectProps) {
  const choices = routeChoices(routes ?? [], currentId);
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <select id={id} className={inputClass} value={value} disabled={disabled || routes === null || !!loadError}
        aria-describedby={hint || loadError ? `${id}-hint` : undefined} onChange={(event) => onChange(event.target.value)}>
        {routes === null && !loadError && <option value={value}>Loading routes...</option>}
        {leadingOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        {choices.map((route) => <option key={route.id} value={route.id}>{route.name}</option>)}
      </select>
      {loadError
        ? <p id={`${id}-hint`} className="mt-1.5 text-xs text-red-600">{loadError}</p>
        : hint && <p id={`${id}-hint`} className="mt-1.5 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

interface RoutePropagationDialogProps {
  entity: 'customer' | 'property';
  /** null = there was no default */
  oldName: string | null;
  /** null = clearing the default */
  newName: string | null;
  busy?: boolean;
  onConfirm: (propagation: types.RoutePropagation) => void;
  onCancel: () => void;
}

/**
 * Asked when an existing Customer / Property default route changes. NONE is
 * preselected; completed, invoiced and cancelled jobs are never changed.
 */
export function RoutePropagationDialog({ entity, oldName, newName, busy, onConfirm, onCancel }: RoutePropagationDialogProps) {
  const [choice, setChoice] = useState<types.RoutePropagation>('NONE');
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.querySelector<HTMLInputElement>('input[type="radio"]:checked')?.focus();
    return () => origin?.focus();
  }, []);
  useEffect(() => {
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) { event.stopImmediatePropagation(); onCancel(); }
      if (event.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll<HTMLElement>('input:not([disabled]), button:not([disabled])');
      if (!focusable?.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', keydown, true);
    return () => window.removeEventListener('keydown', keydown, true);
  }, [busy, onCancel]);

  const choices = propagationChoices(entity, oldName);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div ref={panel} role="dialog" aria-modal="true" aria-labelledby="route-propagation-title" aria-describedby="route-propagation-detail"
        className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl">
        <h2 id="route-propagation-title" className="text-lg font-semibold text-gray-900">Update existing jobs?</h2>
        <p id="route-propagation-detail" className="mt-2 text-sm text-gray-700">
          The default route changes from <strong>{oldName ?? 'no route'}</strong> to <strong>{newName ?? 'no route'}</strong>. Completed, invoiced and cancelled jobs never change.
        </p>
        <fieldset className="mt-4 space-y-2" disabled={busy}>
          <legend className="sr-only">Existing jobs</legend>
          {choices.map((option) => (
            <label key={option.value} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${choice === option.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
              <input type="radio" name="route-propagation" value={option.value} checked={choice === option.value}
                onChange={() => setChoice(option.value)} className="mt-1 h-4 w-4 text-blue-600" />
              <span>
                <span className="block text-sm font-medium text-gray-900">{option.label}</span>
                <span className="block text-xs text-gray-600">{option.description}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className={primaryButtonClass} disabled={busy} onClick={() => onConfirm(choice)}>{busy ? 'Saving...' : 'Save route'}</button>
        </div>
      </div>
    </div>
  );
}

interface DefaultRoutePanelProps {
  entity: 'customer' | 'property';
  entityId: string;
  routeId: string | null;
  api: Partial<Pick<IDispatchApi, 'listDispatchRoutes' | 'setRouteDefault'>>;
  /** Office roles only: the route endpoints are ADMIN / OFFICE_MANAGER */
  canEdit: boolean;
  onSaved: (routeId: string | null) => void;
  className?: string;
  headingClassName?: string;
}

/** The Customer / Property page's default Dispatch route, with an inline change that asks about existing jobs. */
export function DefaultRoutePanel({ entity, entityId, routeId, api, canEdit, onSaved, className, headingClassName }: DefaultRoutePanelProps) {
  const { routes, error: loadError } = useDispatchRoutes(api, canEdit);
  const [value, setValue] = useState(routeId ?? '');
  const [prompt, setPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => { setValue(routeId ?? ''); }, [routeId]);

  if (!canEdit) return null;
  const nextId = value || null;
  const changed = nextId !== (routeId ?? null);
  const save = async (routePropagation: types.RoutePropagation) => {
    if (!api.setRouteDefault) return;
    try {
      setBusy(true); setError(null); setNotice(null);
      const result = await api.setRouteDefault(entity, entityId, { routeId: nextId, routePropagation });
      setPrompt(false);
      setNotice(routeDefaultNotice(result));
      onSaved(result.routeId);
    } catch (err) {
      setPrompt(false);
      setError(getErrorMessage(err, 'Could not change the default route'));
    } finally { setBusy(false); }
  };

  return (
    <section aria-labelledby={`${entity}-route-heading`} className={className}>
      <h2 id={`${entity}-route-heading`} className={headingClassName}>Dispatch route</h2>
      <p className="mt-1 mb-3 text-sm text-gray-500">
        Default route for new jobs {entity === 'customer' ? 'for this customer (a property default wins)' : 'at this property'}. Currently: <strong className="text-gray-900">{routeName(routes, routeId, 'No default route')}</strong>.
      </p>
      {notice && <Toast kind="success">{notice}</Toast>}
      {error && <Toast kind="error">{error}</Toast>}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="sm:w-72">
          <RouteSelect id={`${entity}-default-route`} label="Default route" value={value} onChange={setValue} routes={routes}
            currentId={routeId} leadingOptions={[{ value: '', label: 'No default route' }]} disabled={busy} loadError={loadError} />
        </div>
        <button type="button" className={secondaryButtonClass} disabled={busy || !changed || !!loadError || routes === null}
          onClick={() => { setError(null); setNotice(null); setPrompt(true); }}>
          Change route
        </button>
      </div>
      {prompt && (
        <RoutePropagationDialog entity={entity} oldName={routeId ? routeName(routes, routeId) : null} newName={nextId ? routeName(routes, nextId) : null} busy={busy}
          onConfirm={(choice) => void save(choice)} onCancel={() => { setPrompt(false); setValue(routeId ?? ''); }} />
      )}
    </section>
  );
}
